import React from 'react';
import { Modal } from "../../../components/ui/modal";
import { DollarSign, Calendar, Truck, Tag, ShoppingBag, CreditCard, FileText, ClipboardList } from 'lucide-react';
import useApi from "../../../hooks/useApi";
import Button from "../../../components/ui/button/Button";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface DetailGastoModalProps {
  isOpen: boolean;
  onClose: () => void;
  gasto: any; // TODO: Replace with proper Gasto type
}

const DetailGastoModal: React.FC<DetailGastoModalProps> = ({ isOpen, onClose, gasto }) => {
  const { formatCurrency } = useApi();

  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: es });
    } catch (error) {
      return dateString || 'Fecha no disponible';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalle del Gasto"
      size="lg"
    >
      <div className="space-y-6">
        {/* Información General */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
            <FileText className="w-4 h-4 mr-2 text-primary-500" />
            Información General
          </h3>
          
          <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div className="flex items-start">
              <Calendar className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="ml-3">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Fecha</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                  {formatDate(gasto.fecha)}
                </span>
              </div>
            </div>
            
            <div className="flex items-start">
              <DollarSign className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="ml-3">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Monto</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                  {formatCurrency(gasto.monto)}
                </span>
              </div>
            </div>
            
            <div className="flex items-start">
              <Truck className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="ml-3">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Proveedor</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                  {gasto.proveedor || 'No especificado'}
                </span>
              </div>
            </div>
            
            <div className="flex items-start">
              <CreditCard className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="ml-3">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Método de Pago</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                  {gasto.metodoPago ? gasto.metodoPago.charAt(0).toUpperCase() + gasto.metodoPago.slice(1) : 'No especificado'}
                </span>
              </div>
            </div>
            
            <div className="flex items-start md:col-span-2">
              <ShoppingBag className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="ml-3">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Descripción</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                  {gasto.descripcion || 'Sin descripción'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Categorización */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
            <Tag className="w-4 h-4 mr-2 text-primary-500" />
            Categorización
          </h3>
          
          <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div className="flex items-start">
              <Tag className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="ml-3">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Categoría</span>
                <div className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                    ${gasto.categoria === 'transporte' ? 'bg-purple-100 text-purple-800 dark:bg-purple-800/20 dark:text-purple-500' : ''}
                    ${gasto.categoria === 'suministros' ? 'bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-500' : ''}
                    ${gasto.categoria === 'comisiones' ? 'bg-amber-100 text-amber-800 dark:bg-amber-800/20 dark:text-amber-500' : ''}
                    ${gasto.categoria === 'servicios' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-800/20 dark:text-emerald-500' : ''}
                    ${gasto.categoria === 'equipos' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800/20 dark:text-indigo-500' : ''}
                    ${gasto.categoria === 'alquiler' ? 'bg-rose-100 text-rose-800 dark:bg-rose-800/20 dark:text-rose-500' : ''}
                    ${gasto.categoria === 'otros' || !gasto.categoria ? 'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-500' : ''}
                  `}>
                    {gasto.categoria ? gasto.categoria.charAt(0).toUpperCase() + gasto.categoria.slice(1) : 'No categorizado'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <FileText className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="ml-3">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Comprobante</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                  {gasto.comprobante || 'Sin comprobante'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Notas Adicionales */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
            <ClipboardList className="w-4 h-4 mr-2 text-primary-500" />
            Notas Adicionales
          </h3>
          
          <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
              {gasto.notas || 'No se han añadido notas a este gasto.'}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DetailGastoModal; 